// Airtime voucher inventory for the Donkey Mile → airtime feature (wave 3:
// tiles, two cards and the modern table).
//
// Three sections:
//  1. Per provider+denomination Available/Used counters — read from the tiny
//     /phone_cards_meta node (never the card pool).
//  2. Batch import: paste codes one per line → import_phone_cards Cloud
//     Function (Bearer auth, dry-run preview by default; a real import asks
//     first). Even admins cannot write /phone_cards directly — rules are
//     server-only.
//  3. On-demand cards table: loads ONE provider+denomination subtree only on
//     explicit click (Load cards). Codes are masked (last 4 only) with a
//     per-row eye reveal; codes are never searched, exported or printed.

import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import SimCardOutlinedIcon from '@mui/icons-material/SimCardOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import UploadRoundedIcon from '@mui/icons-material/UploadRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import RemoveCircleOutlineRoundedIcon from '@mui/icons-material/RemoveCircleOutlineRounded';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import { api } from 'common';
import DashboardCard from '../components/DashboardCard';
import ClickableUserLink from '../components/ClickableUserLink';
import { confirmDialog, InlineMessage, EmptyState } from '../components/feedback';
import { DataTable, PageHeader, SectionCard, FieldLabel, FieldError, HintIcon, ChipGroup, formatNumber } from '../components/modern';
import { tokens as c } from '../theme';

// labelKey: the live pack's name for the provider; label is the English default.
const PROVIDERS = [
  { key: 'ethiotel', labelKey: 'ethio_telecom', label: 'Ethio Telecom' },
  { key: 'safaricom', labelKey: 'safaricom', label: 'Safaricom' },
];

const MAX_CODES = 2000;

export default function PhoneCards() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const settings = useSelector((state) => state.settingsdata.settings);
  const phonecarddata = useSelector((state) => state.phonecarddata);
  const isAdmin = auth?.profile?.usertype === 'admin';
  const symbol = settings?.symbol || 'ETB';

  const denominations = String(settings?.airtimeDenominations || '50,100,200')
    .split(',')
    .map((s) => parseInt(s.trim(), 10))
    .filter((n) => n > 0);

  const providerLabel = (key) => {
    const p = PROVIDERS.find((x) => x.key === key);
    return p ? t(p.labelKey, { defaultValue: p.label }) : key;
  };

  // Section 1 — meta counters
  const [meta, setMeta] = useState(null);
  const [metaBusy, setMetaBusy] = useState(false);
  const loadMeta = useCallback(async () => {
    setMetaBusy(true);
    try {
      const m = await api.fetchPhoneCardsMeta();
      setMeta(m || {});
    } catch (_) {
      setMeta({});
    } finally {
      setMetaBusy(false);
    }
  }, []);
  useEffect(() => {
    if (isAdmin) loadMeta();
  }, [isAdmin, loadMeta]);

  // Section 2 — import panel
  const [importProvider, setImportProvider] = useState('ethiotel');
  const [importValue, setImportValue] = useState(denominations[0] || 50);
  const [codesText, setCodesText] = useState('');
  const [dryRun, setDryRun] = useState(true);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const parsedCodes = codesText
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
  const uniqueCount = new Set(parsedCodes).size;
  const tooMany = parsedCodes.length > MAX_CODES;

  const runImport = async () => {
    setImporting(true);
    setImportResult(null);
    try {
      const result = await api.importPhoneCards({
        provider: importProvider,
        value: importValue,
        codes: parsedCodes,
        dryRun,
      });
      setImportResult({ success: true, ...result });
      if (!dryRun) {
        setCodesText('');
        loadMeta();
      }
    } catch (error) {
      setImportResult({ error: error.message, detail: error.detail, hint: error.hint });
    } finally {
      setImporting(false);
    }
  };

  const handleImportClick = async () => {
    if (parsedCodes.length === 0 || tooMany || importing) return;
    if (!dryRun) {
      const ok = await confirmDialog({
        title: t('confirm_import_title', { defaultValue: 'Confirm import' }),
        body: t('confirm_import_msg', {
          count: uniqueCount,
          provider: providerLabel(importProvider),
          value: importValue,
          defaultValue: 'Import {{count}} codes as {{provider}} {{value}} ETB? This cannot be undone.',
        }),
        confirmLabel: t('import_now', { defaultValue: 'Import now' }),
        cancelLabel: t('cancel', { defaultValue: 'Cancel' }),
        destructive: true,
      });
      if (!ok) return;
    }
    runImport();
  };

  // Section 3 — cards table (on demand)
  const [tableProvider, setTableProvider] = useState('ethiotel');
  const [tableValue, setTableValue] = useState(denominations[0] || 50);
  const [loadedFor, setLoadedFor] = useState(null); // { provider, value } of the last Load cards
  const [statusFilter, setStatusFilter] = useState('all');
  const [revealed, setRevealed] = useState({});

  const loadCards = () => {
    setLoadedFor({ provider: tableProvider, value: tableValue });
    setRevealed({});
    dispatch(api.fetchPhoneCards(tableProvider, tableValue));
  };

  const allCards = phonecarddata.cards || [];
  const cards = allCards.filter((card) => statusFilter === 'all' || card.status === statusFilter);
  const counts = {
    all: allCards.length,
    available: allCards.filter((card) => card.status === 'available').length,
    used: allCards.filter((card) => card.status === 'used').length,
  };

  const masked = (code) => `•••• ${String(code || '').slice(-4)}`;

  if (!isAdmin) {
    return (
      <Paper sx={{ p: 2 }}>
        <EmptyState icon={<SimCardOutlinedIcon />} title={t('admin_access_required', { defaultValue: 'Admin access required.' })} />
      </Paper>
    );
  }

  const columns = [
    {
      title: t('status', { defaultValue: 'Status' }),
      field: 'status',
      type: 'status',
      status: (row) =>
        row.status === 'available'
          ? { tone: 'success', icon: <CheckCircleOutlineRoundedIcon />, label: t('available', { defaultValue: 'Available' }) }
          : { tone: 'neutral', icon: <RemoveCircleOutlineRoundedIcon />, label: t('used', { defaultValue: 'Used' }) },
      searchable: false,
    },
    {
      title: t('card_code', { defaultValue: 'Code' }),
      field: 'code',
      sortable: false,
      // Only what is on screen without a reveal: the last four characters.
      searchValue: (row) => String(row.code || '').slice(-4),
      export: false,
      render: (row) => (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap' }}>
          <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums', letterSpacing: '0.04em', fontWeight: 600 }}>
            {revealed[row.id] ? row.code : masked(row.code)}
          </Box>
          <Tooltip title={revealed[row.id] ? t('hide_code', { defaultValue: 'Hide code' }) : t('reveal_code', { defaultValue: 'Reveal code' })}>
            <IconButton
              size="small"
              aria-label={revealed[row.id] ? t('hide_code', { defaultValue: 'Hide code' }) : t('reveal_code', { defaultValue: 'Reveal code' })}
              aria-pressed={revealed[row.id] ? 'true' : 'false'}
              onClick={() => setRevealed({ ...revealed, [row.id]: !revealed[row.id] })}
            >
              {revealed[row.id] ? <VisibilityOffOutlinedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
    { title: t('imported_at', { defaultValue: 'Imported' }), field: 'importedAt', type: 'datetime', defaultSort: 'desc' },
    { title: t('imported_by', { defaultValue: 'Imported by' }), field: 'importedBy' },
    {
      title: t('used_by', { defaultValue: 'Used by' }),
      field: 'usedBy',
      render: (row) => (row.usedBy ? <ClickableUserLink value={row.usedBy} type="customer" userId={row.usedBy} /> : null),
    },
    { title: t('used_at', { defaultValue: 'Used at' }), field: 'usedAt', type: 'datetime' },
  ];

  const providerSelect = (id, value, onChange) => (
    <TextField id={id} select fullWidth value={value} onChange={(e) => onChange(e.target.value)} sx={{ minWidth: 0 }}>
      {PROVIDERS.map((p) => (
        <MenuItem key={p.key} value={p.key}>
          {providerLabel(p.key)}
        </MenuItem>
      ))}
    </TextField>
  );
  const denominationSelect = (id, value, onChange) => (
    <TextField id={id} select fullWidth value={value} onChange={(e) => onChange(e.target.value)} sx={{ minWidth: 0 }}>
      {denominations.map((d) => (
        <MenuItem key={d} value={d}>
          {d} {symbol}
        </MenuItem>
      ))}
    </TextField>
  );
  const pairGrid = { display: 'grid', columnGap: 2, rowGap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 220px))' } };

  const result = importResult;
  const tableTitle = loadedFor ? `${providerLabel(loadedFor.provider)} · ${loadedFor.value} ${symbol}` : t('cards_table_title', { defaultValue: 'Cards' });

  return (
    <Box dir={i18n.dir()} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <PageHeader
        secondaryActions={[
          {
            key: 'refresh',
            label: t('refresh_counts', { defaultValue: 'Refresh counts' }),
            icon: metaBusy ? <CircularProgress size={16} color="inherit" /> : <RefreshRoundedIcon />,
            onClick: loadMeta,
            disabled: metaBusy,
          },
        ]}
      />

      {/* Section 1 — counters from /phone_cards_meta */}
      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))', lg: 'repeat(6, minmax(0, 1fr))' } }}>
        {PROVIDERS.map((p) =>
          denominations.map((d) => {
            const m = meta?.[p.key]?.[d] || meta?.[p.key]?.[String(d)] || {};
            return (
              <DashboardCard
                key={`${p.key}_${d}`}
                icon={<SimCardOutlinedIcon />}
                hue={p.key === 'safaricom' ? 'success' : 'info'}
                title={`${providerLabel(p.key)} · ${d} ${symbol}`}
                loading={meta === null}
                stats={[
                  { label: t('available', { defaultValue: 'Available' }), value: formatNumber(parseInt(m.available, 10) || 0) },
                  { label: t('used', { defaultValue: 'Used' }), value: formatNumber(parseInt(m.used, 10) || 0) },
                ]}
              />
            );
          })
        )}
      </Box>

      {/* Section 2 — batch import */}
      <SectionCard
        id="pc-import"
        icon={<UploadRoundedIcon />}
        hue="accent"
        title={t('import_phone_cards_title', { defaultValue: 'Import Voucher Codes' })}
        action={
          <HintIcon
            id="pc-import-help"
            hint={t('import_phone_cards_help', {
              defaultValue:
                'Paste the codes for ONE provider and ONE denomination per batch, one code per line. Run a dry run first — it previews duplicates and invalid lines without writing anything.',
            })}
          />
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25, mt: 1.5 }}>
          <Box sx={pairGrid}>
            <Box sx={{ minWidth: 0 }}>
              <FieldLabel htmlFor="pc-import-provider">{t('provider', { defaultValue: 'Provider' })}</FieldLabel>
              {providerSelect('pc-import-provider', importProvider, setImportProvider)}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <FieldLabel htmlFor="pc-import-value">{t('denomination', { defaultValue: 'Denomination' })}</FieldLabel>
              {denominationSelect('pc-import-value', importValue, setImportValue)}
            </Box>
          </Box>
          <Box>
            <FieldLabel id="pc-mode-label">{t('phone_cards_mode', { defaultValue: 'Mode' })}</FieldLabel>
            <ToggleButtonGroup
              exclusive
              color="primary"
              value={dryRun ? 'dry' : 'real'}
              onChange={(e, v) => {
                if (v) setDryRun(v === 'dry');
              }}
              aria-labelledby="pc-mode-label"
              sx={{ flexWrap: 'wrap' }}
            >
              <ToggleButton value="dry" sx={{ textTransform: 'none', fontWeight: 600, px: 2 }}>
                {t('dry_run_preview', { defaultValue: 'Dry run (preview only — no writes)' })}
              </ToggleButton>
              <ToggleButton value="real" sx={{ textTransform: 'none', fontWeight: 600, px: 2 }}>
                {t('real_import_writes', { defaultValue: 'Real import (writes to inventory)' })}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Box>
            <FieldLabel htmlFor="pc-codes">{t('paste_codes_hint', { defaultValue: 'Paste voucher codes, one per line' })}</FieldLabel>
            <TextField
              id="pc-codes"
              multiline
              minRows={8}
              maxRows={20}
              fullWidth
              value={codesText}
              onChange={(e) => setCodesText(e.target.value)}
              error={tooMany}
              inputProps={{
                spellCheck: false,
                autoComplete: 'off',
                'aria-describedby': `pc-codes-count${tooMany ? ' pc-codes-error' : ''}`,
              }}
              sx={{ '& textarea': { fontVariantNumeric: 'tabular-nums', letterSpacing: '0.02em' } }}
            />
            <Typography id="pc-codes-count" role="status" aria-live="polite" sx={{ mt: 0.75, fontSize: 13.5, color: c.TEXT_3 }}>
              {t('codes_detected', { count: parsedCodes.length, defaultValue: '{{count}} codes detected' })}
              {parsedCodes.length !== uniqueCount
                ? ` · ${parsedCodes.length - uniqueCount} ${t('duplicates_in_paste', { defaultValue: 'duplicate lines' })}`
                : ''}
            </Typography>
            <FieldError id="pc-codes-error">{tooMany ? t('too_many_codes', { defaultValue: 'Maximum 2000 codes per batch — split the list.' }) : null}</FieldError>
          </Box>
          <Box>
            <Button
              variant="contained"
              startIcon={importing ? <CircularProgress size={16} color="inherit" /> : <UploadRoundedIcon />}
              disabled={importing || parsedCodes.length === 0 || tooMany}
              onClick={handleImportClick}
            >
              {importing
                ? dryRun
                  ? t('previewing', { defaultValue: 'Previewing…' })
                  : t('importing', { defaultValue: 'Importing…' })
                : dryRun
                  ? t('preview_import', { defaultValue: 'Preview import' })
                  : t('import_now', { defaultValue: 'Import now' })}
            </Button>
          </Box>

          {result ? (
            result.error ? (
              <InlineMessage kind="error" onClose={() => setImportResult(null)}>
                {result.error}
                {result.detail ? <div>{String(result.detail)}</div> : null}
                {result.hint ? <div>{String(result.hint)}</div> : null}
              </InlineMessage>
            ) : (
              <InlineMessage kind={result.dryRun ? 'info' : 'success'} onClose={() => setImportResult(null)}>
                {result.dryRun
                  ? `${t('dry_run', { defaultValue: 'Dry run' })}: ${result.wouldImport} ${t('would_import', { defaultValue: 'codes would be imported' })}`
                  : `${t('import_success_count', { defaultValue: 'Imported' })}: ${result.written}`}
                {' · '}
                {t('received', { defaultValue: 'received' })}: {result.received}
                {' · '}
                {t('duplicates_skipped', { defaultValue: 'duplicates skipped' })}: {(result.duplicatesInBatch || 0) + (result.duplicatesExisting || 0)}
                {' · '}
                {t('invalid_lines', { defaultValue: 'invalid lines' })}: {result.invalidCount || 0}
                {result.dryRun && result.sample?.length ? (
                  <Box sx={{ mt: 0.75, fontVariantNumeric: 'tabular-nums', overflowWrap: 'anywhere' }}>
                    {t('sample', { defaultValue: 'Sample' })}: {result.sample.join(', ')}
                  </Box>
                ) : null}
              </InlineMessage>
            )
          ) : null}
        </Box>
      </SectionCard>

      {/* Section 3 — cards table, loaded on demand */}
      <SectionCard id="pc-cards" icon={<ConfirmationNumberOutlinedIcon />} hue="info" title={t('cards_table_title', { defaultValue: 'Cards' })}>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', flexWrap: 'wrap', gap: 2, mt: 1.5 }}>
          <Box sx={{ width: { xs: '100%', sm: 220 } }}>
            <FieldLabel htmlFor="pc-table-provider">{t('provider', { defaultValue: 'Provider' })}</FieldLabel>
            {providerSelect('pc-table-provider', tableProvider, setTableProvider)}
          </Box>
          <Box sx={{ width: { xs: '100%', sm: 220 } }}>
            <FieldLabel htmlFor="pc-table-value">{t('denomination', { defaultValue: 'Denomination' })}</FieldLabel>
            {denominationSelect('pc-table-value', tableValue, setTableValue)}
          </Box>
          <Button
            variant="outlined"
            color="neutral"
            onClick={loadCards}
            disabled={phonecarddata.loading}
            startIcon={phonecarddata.loading ? <CircularProgress size={16} color="inherit" /> : <ConfirmationNumberOutlinedIcon />}
            sx={{ minHeight: 44 }}
          >
            {t('load_cards', { defaultValue: 'Load cards' })}
          </Button>
        </Box>
      </SectionCard>

      {loadedFor ? (
        <DataTable
          title={tableTitle}
          rows={cards}
          columns={columns}
          rowKey="id"
          loading={!!phonecarddata.loading}
          error={
            phonecarddata.error && phonecarddata.error.flag && !phonecarddata.loading
              ? { title: t('phone_cards_load_failed', { defaultValue: 'Could not load the cards.' }), message: phonecarddata.error.msg, onRetry: loadCards }
              : null
          }
          countText={({ shown, total, query, loading }) =>
            loading
              ? t('loading', { defaultValue: 'Loading…' })
              : query
                ? t('table_count_shown', { shown, total, defaultValue: '{{shown}} of {{total}}' })
                : `${tableTitle} · ${t('phone_cards_count', {
                    count: total,
                    defaultValue_one: '{{count}} card',
                    defaultValue_other: '{{count}} cards',
                    defaultValue: '{{count}} cards',
                  })}`
          }
          headerContent={
            <ChipGroup
              label={t('status', { defaultValue: 'Status' })}
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { value: 'all', label: t('all', { defaultValue: 'All' }), count: counts.all },
                { value: 'available', label: t('available', { defaultValue: 'Available' }), count: counts.available },
                { value: 'used', label: t('used', { defaultValue: 'Used' }), count: counts.used },
              ]}
            />
          }
          searchPlaceholder={t('phone_cards_search', { defaultValue: 'Search last 4, imported or used by' })}
          rowLabel={(row) => masked(row.code)}
          prefsKey="phone_cards"
          minWidth={860}
          emptyState={({ query }) =>
            query
              ? null
              : {
                  icon: <ConfirmationNumberOutlinedIcon />,
                  title:
                    statusFilter !== 'all'
                      ? t('phone_cards_empty_status', { defaultValue: 'No cards with this status' })
                      : t('phone_cards_empty', { defaultValue: 'No cards for this provider and denomination yet' }),
                  actionLabel: statusFilter !== 'all' ? t('clear_filters', { defaultValue: 'Clear filters' }) : undefined,
                  onAction: statusFilter !== 'all' ? () => setStatusFilter('all') : undefined,
                }
          }
        />
      ) : (
        <Paper sx={{ p: 2 }}>
          <EmptyState
            compact
            icon={<ConfirmationNumberOutlinedIcon />}
            title={t('load_cards_hint', { defaultValue: 'Pick a provider and denomination, then Load cards.' })}
          />
        </Paper>
      )}
    </Box>
  );
}
